import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronDown,
  Filter,
  Lightbulb,
  ListFilter,
  X,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CardCurrency from '@/components/shared/CardCurrency/CardCurrency';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import type { UTMGroup } from '@/components/shared/UTMGroupCard/UTMGroupCard';
import UTMGroupCard, { UTMGroupCardSkeleton } from '@/components/shared/UTMGroupCard/UTMGroupCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import LayoutInside from '@/layout/LayoutInside';
import { formatCurrency } from '@/lib/select-options';
import { cn } from '@/lib/utils';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import useModalStore from '@/store/useModalStore';

import {
  FILTER_UTM_PANEL_MODAL,
  getGroupDescription,
  MAIN_GROUP_KEYS,
  UTM_PARAMS,
} from './constants';
import { useUtmPanel } from './hooks/useUtmPanel';
import FilterUtmPanel from './Partials/FilterUtmPanel';
import UtmParameterFilter from './Partials/UtmParameterFilter';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

const contentVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: { height: { duration: 0.3, ease: 'easeInOut' }, opacity: { duration: 0.2 } },
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: 'easeInOut' },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
};

const mapOptionToUrl = (opt: { label: string; value: string }) =>
  `${opt.label}:::${opt.value}`;

export default function UTMPanel() {
  const { t } = useTranslation(['reports', 'common', 'sales']);
  const { openModal, closeModal, modals } = useModalStore();

  const {
    data,
    isFetching,
    showSkeleton,
    summary,
    dateRange,
    handleDateRangeChange,
    filterParams,
    filterVisualParams,
    setFilterParams,
    removeFilterItem,
    clearAllFilters,
    keyLabel,
    searchParam,
    searchValue,
    setSearchParam,
    setSearchValue,
    hasAnyFilter,
    globalFilterCount,
    crossFilterCount,
    showAdvanced,
    setShowAdvanced,
    activeFilters,
    allSelectedValues,
    handleToggleValue,
    currency,
  } = useUtmPanel();

  const zeroMoney = useMemo(() => formatCurrency(0, currency), [currency]);

  const groups: UTMGroup[] = useMemo(() => {
    if (!data?.groups) return [];
    const allGroups = [
      ...data.groups.map((g: any) => ({
        key: g.key,
        label: g.label,
        description: getGroupDescription(g.key),
        total_values: g.total_values,
        entries: g.entries ?? [],
      })),
    ];
    if (data?.advanced_groups) {
      const srcGroup = data.advanced_groups.find((g: any) => g.key === 'src');
      if (srcGroup) {
        allGroups.push({
          key: srcGroup.key,
          label: srcGroup.label,
          description: getGroupDescription(srcGroup.key),
          total_values: srcGroup.total_values,
          entries: srcGroup.entries ?? [],
        });
      }
    }
    return allGroups;
  }, [data]);

  const advancedGroups: UTMGroup[] = useMemo(() => {
    if (!data?.advanced_groups) return [];
    return data.advanced_groups
      .filter((g: any) => g.key !== 'src')
      .map((g: any) => ({
        key: g.key,
        label: g.label,
        description: getGroupDescription(g.key),
        total_values: g.total_values,
        entries: g.entries ?? [],
      }));
  }, [data]);

  const globalFilterBadges = useMemo(() => {
    const badges: {
      key: string;
      group: string;
      label: string;
      onClick: () => void;
    }[] = [];

    Object.entries(filterVisualParams).forEach(([key, values]) => {
      if (key === 'search_param') return;

      if (key === 'search_value' && typeof values === 'string' && values !== '') {
        const paramLabel =
          UTM_PARAMS.find((p) => p.value === searchParam)?.label ?? searchParam;
        badges.push({
          key: 'search_value',
          group: `${keyLabel.search_value}:`,
          label: `${paramLabel}: "${values}"`,
          onClick: () => removeFilterItem('search_value', 0),
        });
        return;
      }

      if (Array.isArray(values) && values.length > 0) {
        (values as { label?: string; value?: string }[]).forEach((value, index) => {
          badges.push({
            key: `${key}-${index}`,
            group: `${keyLabel[key]}:`,
            label: `${value?.label || value?.value || value}`,
            onClick: () => removeFilterItem(key, index),
          });
        });
      }
    });

    return badges;
  }, [filterVisualParams, keyLabel, removeFilterItem, searchParam]);

  const breadcrumbItems = [
    { text: t('common:reports'), link: '/reports' },
    { text: t('reports:utmPanel.title'), link: '/reports/utm-panel' },
  ];

  const sheetFilterValues = {
    products: (filterParams.products as string[] | undefined)?.map((p) => {
      const [label, value] = p.includes(':::') ? p.split(':::') : [p, p];
      return { label, value };
    }),
    offers: (filterParams.offers as string[] | undefined)?.map((o) => {
      const [label, value] = o.includes(':::') ? o.split(':::') : [o, o];
      return { label, value };
    }),
    checkouts: (filterParams.checkouts as string[] | undefined)?.map((c) => {
      const [label, value] = c.includes(':::') ? c.split(':::') : [c, c];
      return { label, value };
    }),
    affiliates: (filterParams.affiliates as string[] | undefined)?.map((a) => {
      const [label, value] = a.includes(':::') ? a.split(':::') : [a, a];
      return { label, value };
    }),
    coproducers: (filterParams.coproducers as string[] | undefined)?.map((c) => {
      const [label, value] = c.includes(':::') ? c.split(':::') : [c, c];
      return { label, value };
    }),
    templates: (filterParams.templates as string[] | undefined)?.map((c) => {
      const [label, value] = c.includes(':::') ? c.split(':::') : [c, c];
      return { label, value };
    }),
  };

  return (
    <LayoutInside hasBgBlur items={breadcrumbItems}>
      <CustomSheet
        open={modals[FILTER_UTM_PANEL_MODAL]}
        onClose={() => closeModal(FILTER_UTM_PANEL_MODAL)}
      >
        {modals[FILTER_UTM_PANEL_MODAL] && (
          <FilterUtmPanel
            filterValues={sheetFilterValues}
            onFilter={(formData) => {
              setFilterParams({
                products: formData.products?.map(mapOptionToUrl),
                offers: formData.offers?.map(mapOptionToUrl),
                checkouts: formData.checkouts?.map(mapOptionToUrl),
                affiliates: formData.affiliates?.map(mapOptionToUrl),
                coproducers: formData.coproducers?.map(mapOptionToUrl),
                templates: formData.templates?.map(mapOptionToUrl),
                search_param: searchParam,
                search_value: searchValue || undefined,
              });
            }}
          />
        )}
      </CustomSheet>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid gap-6"
      >
        <Link to="/reports" className="w-fit">
          <Button
            variant="default"
            className="bg-transparent text-colorText hover:bg-transparent p-0"
          >
            <ArrowLeft size={16} />
            {t('common:back')}
          </Button>
        </Link>

        <div>
          <h1 className="text-lg font-semibold">{t('reports:utmPanel.title')}</h1>
          <p className="text-xs text-zinc-500">{t('reports:utmPanel.subtitle')}</p>
        </div>

        <motion.div variants={itemVariants}>
          <Card className="p-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full">
              <div className="flex-shrink-0 w-full sm:w-auto">
                <DateRangePicker
                  onChange={handleDateRangeChange}
                  defaultValue={dateRange}
                />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <UtmParameterFilter
                  searchParam={searchParam}
                  searchValue={searchValue}
                  onSearchParamChange={setSearchParam}
                  onSearchValueChange={setSearchValue}
                />
              </div>
              <Button
                className="hidden md:flex flex-shrink-0"
                variant="secondary"
                onClick={() => openModal(FILTER_UTM_PANEL_MODAL)}
                data-cy="open-filter-utm-panel"
              >
                <ListFilter size={24} />
                {t('sales:filter')}
              </Button>
              <Button
                className="flex md:hidden flex-shrink-0"
                variant="secondary"
                onClick={() => openModal(FILTER_UTM_PANEL_MODAL)}
              >
                <ListFilter size={24} />
              </Button>
            </div>

            <AnimatePresence>
              {hasAnyFilter && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#27272a]">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <Filter className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                      <span className="text-xs text-zinc-500">
                        {t('reports:utmPanel.active_filters')}:
                      </span>

                      {globalFilterBadges.map((badge) => (
                        <Badge
                          key={badge.key}
                          variant="secondary"
                          className="text-[10px] gap-1 cursor-pointer hover:bg-red-500/20 max-w-[200px]"
                          onClick={badge.onClick}
                        >
                          <span className="text-zinc-400">{badge.group}</span>
                          <span className="truncate">{badge.label}</span>
                          <X className="h-2.5 w-2.5 flex-shrink-0" />
                        </Badge>
                      ))}

                      {Array.from(activeFilters.entries()).map(([key, values]) =>
                        Array.from(values).map((v) => (
                          <Badge
                            key={`${key}:${v}`}
                            variant="secondary"
                            className="text-[10px] gap-1 cursor-pointer hover:bg-red-500/20 max-w-[200px]"
                            onClick={() => handleToggleValue(key, v)}
                          >
                            <span className="text-zinc-400">{key}:</span>
                            <span className="truncate">
                              {v.length > 20 ? `${v.slice(0, 20)}…` : v}
                            </span>
                            <X className="h-2.5 w-2.5 flex-shrink-0" />
                          </Badge>
                        )),
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={clearAllFilters}
                      className="ml-2 h-7 flex items-center gap-1.5 bg-transparent text-interactive flex-shrink-0"
                    >
                      {t('reports:utmPanel.clear_filters', {
                        count: globalFilterCount + crossFilterCount || 1,
                      })}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <CardCurrency
            value={summary?.net_total ?? zeroMoney}
            status="paid"
            text={t('reports:utmPanel.net_total')}
            subtitle={`${t('reports:utmPanel.gross')}: ${summary?.gross_total ?? zeroMoney}`}
            isLoading={showSkeleton}
          />
          <CardCurrency
            value={String(summary?.sales_count ?? 0)}
            status="awaiting_payment"
            text={t('reports:utmPanel.sales_count')}
            subtitle={t('reports:utmPanel.records_in_filter')}
            isLoading={showSkeleton}
          />
          <CardCurrency
            value={summary?.avg_ticket_net ?? zeroMoney}
            status="paid"
            text={t('reports:utmPanel.avg_ticket')}
            subtitle={t('reports:utmPanel.avg_per_sale')}
            isLoading={showSkeleton}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="p-5 border-primary/20 bg-primary/5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-primary/30 flex-shrink-0">
                <Lightbulb className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-white mb-3">
                  {t('reports:utmPanel.how_it_works')}
                </h3>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li className="flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    {t('reports:utmPanel.how_step1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    {t('reports:utmPanel.how_step2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    {t('reports:utmPanel.how_step3')}
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className={cn(
            'grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-fr relative',
            isFetching && !showSkeleton && 'opacity-70 transition-opacity duration-200',
          )}
        >
          {showSkeleton ? (
            <>
              <UTMGroupCardSkeleton fullWidth />
              {Array.from({ length: 5 }).map((_, i) => (
                <UTMGroupCardSkeleton key={i} />
              ))}
            </>
          ) : groups.length > 0 ? (
            groups.map((group) => (
              <UTMGroupCard
                key={group.key}
                group={group}
                selectedValues={allSelectedValues}
                onToggleValue={handleToggleValue}
              />
            ))
          ) : (
            MAIN_GROUP_KEYS.map((key, i) => (
              <UTMGroupCardSkeleton key={key} fullWidth={i === 0} />
            ))
          )}
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card
            className="p-5 md:p-6 cursor-pointer select-none transition-all"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-colorText font-semibold text-base">
                  {t('reports:utmPanel.advanced_params')}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  sub1–sub20
                </Badge>
                <ChevronDown
                  size={16}
                  className={cn(
                    'text-muted-foreground transition-transform duration-300',
                    showAdvanced && 'rotate-180',
                  )}
                />
              </div>
            </div>
          </Card>
        </motion.div>

        <AnimatePresence initial={false}>
          {showAdvanced && (
            <motion.div
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={contentVariants}
              className="overflow-hidden"
            >
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-fr"
              >
                {showSkeleton ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <UTMGroupCardSkeleton key={i} />
                  ))
                ) : (
                  advancedGroups.map((group) => (
                    <UTMGroupCard
                      key={group.key}
                      group={group}
                      selectedValues={allSelectedValues}
                      onToggleValue={handleToggleValue}
                    />
                  ))
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </LayoutInside>
  );
}
